import { useCallback, useMemo, useState } from 'react'
import { AlertCircle, Check, FileDown, Plus, Trash2 } from 'lucide-react'
import { ResponseWithCitations } from '../ResponseWithCitations'
import { parseApplyBlocks, hasApplyBlocks, type ApplyBlock } from '../../lib/apply-blocks'
import { dispatchApplyBlock } from '../../lib/dispatch-apply-block'

interface Source {
  id: string
  title: string
  slug?: string
  sourceType?: string
  publishedUrl?: string | null
  topicId?: string
  docId?: string
  content?: string
  score?: number
  citationNumber?: number
}

interface ApplyableResponseProps {
  content: string
  sources?: Source[]
  className?: string
}

const ACTION_ICON = {
  replace: FileDown,
  'insert-after': Plus,
  delete: Trash2,
} as const

const ACTION_LABEL = {
  replace: 'Apply',
  'insert-after': 'Insert',
  delete: 'Remove',
} as const

/**
 * Renders assistant text with LLM-marked apply blocks.
 *
 * If the text contains <<<APPLY>>> markers, each marker is rendered as a
 * bordered box with an "Apply" button that dispatches a block-targeted event.
 * Plain text between markers renders normally via ResponseWithCitations.
 *
 * Falls back to plain ResponseWithCitations if no markers are found.
 */
export function ApplyableResponse({ content, sources = [], className }: ApplyableResponseProps) {
  const parsed = useMemo(() => {
    if (!hasApplyBlocks(content)) return null
    return parseApplyBlocks(content)
  }, [content])
  // Track which blocks in THIS message have been successfully applied.
  // After a successful apply the block's id in the doc is replaced by a
  // fresh nanoid, so re-clicking the same apply-block would always fail
  // with "block not found". Mark it applied and disable the button.
  const [appliedKeys, setAppliedKeys] = useState<Set<string>>(() => new Set())
  const handleApply = useCallback((block: ApplyBlock, key: string) => {
    if (appliedKeys.has(key)) return
    // Inline <<<APPLY>>> markers carry no policy slug, so this path can't
    // auto-open the editor — it relies on the policy already being open and
    // falls back to the "open the policy" toast otherwise.
    dispatchApplyBlock({
      blockId: block.blockId,
      action: block.action,
      content: block.content,
      label: block.label,
      onResult: (ok) => {
        if (ok) {
          setAppliedKeys((prev) => {
            const next = new Set(prev)
            next.add(key)
            return next
          })
        }
      },
    })
  }, [appliedKeys])

  // No apply markers — render as plain text
  if (!parsed) {
    return <ResponseWithCitations content={content} sources={sources} className={className} />
  }

  return (
    <div className="space-y-2">
      {parsed.segments.map((segment, i) => {
        if (segment.type === 'text') {
          return (
            <ResponseWithCitations
              key={i}
              content={segment.content}
              sources={sources}
              className={className}
            />
          )
        }

        if (segment.type === 'malformed') {
          return (
            <div
              key={i}
              className="flex items-start gap-2 rounded-md border border-status-amber-border bg-status-amber-subtle px-3 py-2 text-[12px] text-status-amber-strong"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>
                <div className="font-medium">Edit suggestion couldn't render</div>
                <div className="text-[11px] text-status-amber-text-muted">
                  Vera proposed an edit but the apply-block format was malformed,
                  so no Apply button is shown. Ask her to retry. Usually a brief
                  "please re-issue that edit" is enough.
                </div>
              </div>
            </div>
          )
        }

        const { block } = segment
        const Icon = ACTION_ICON[block.action]
        const label = ACTION_LABEL[block.action]
        const blockKey = `${i}:${block.blockId}`
        const isApplied = appliedKeys.has(blockKey)

        return (
          <div
            key={i}
            className="rounded-md border border-border-divider bg-card overflow-hidden"
          >
            {/* Header bar */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-background border-b border-border">
              <span className="text-[11px] font-medium text-text-body">
                {block.label}
              </span>
              <button
                type="button"
                onClick={() => handleApply(block, blockKey)}
                disabled={isApplied}
                className={
                  isApplied
                    ? 'flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-medium text-status-green-strong cursor-default'
                    : 'flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium text-cobalt-strong hover:bg-primary-subtle transition-colors cursor-pointer'
                }
              >
                {isApplied ? (
                  <>
                    <Check className="h-3 w-3" />
                    Applied
                  </>
                ) : (
                  <>
                    <Icon className="h-3 w-3" />
                    {label}
                  </>
                )}
              </button>
            </div>
            {/* Content preview */}
            {block.action !== 'delete' && (
              <div className="px-3 py-2 max-h-[200px] overflow-y-auto">
                <ResponseWithCitations
                  content={block.content}
                  sources={sources}
                  className="text-[13px] leading-relaxed"
                />
              </div>
            )}
            {block.action === 'delete' && (
              <div className="px-3 py-2 text-[12px] text-text-placeholder italic">
                This section will be removed from the document.
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
